/**
 * Chart Configuration Utilities
 * Generic utilities for building ECharts configurations
 * Used by: financial-metrics, detailed_view dashboards
 */

import {
  roundOff,
  wrapWithPrefixSuffix,
  capitalizeKey,
  formatDecimalWithCommas,
  oneDecimalWithCommas,
  formatStat,
  formatStatOneDecimal,
} from './format.utils';
import { formatValueSafely, TextCaseMode } from './text-case.utils';

// ============ TYPES ============

export interface ChartConfig {
  showLegend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  legendPageSize?: number;
  legendScroll?: boolean;
  maxLegends?: number;
  showDataLabels?: boolean;
  dataLabelPosition?: 'inside' | 'outside' | 'top' | 'bottom';
  showTotalLabels?: boolean;
  totalLabelPosition?: 'top' | 'bottom';
  totalLabelOffset?: [number, number];
  colorMap?: Record<string, string>;
  topNColorsByStackPosition?: string[];
  barWidth?: string | number;
  showYAxisLabels?: boolean;
  stackType?: 'normal' | 'percentage';
  seriesOrder?: string[];
  stackOrder?: 'asc' | 'desc';
  height?: string;
  orientation?: 'vertical' | 'horizontal';
  numberFormat?: 'value' | 'percentage';
  categoryMatchMode?: 'exact' | 'contains' | 'startsWith' | 'endsWith';
  colorSyncEnabled?: boolean;
  enableFilterSync?: boolean;
  /** Theme: color for data labels rendered inside bars */
  dataLabelInsideColor?: string;
  /** Theme: color for data labels rendered outside bars */
  dataLabelOutsideColor?: string;
  /** Theme: color for the total label series */
  totalLabelColor?: string;
  tooltip?: TooltipConfig;
  yAxis?: AxisConfig;
  /** For dual-axis charts (line-column) */
  yAxisRight?: AxisConfig;
}

export interface TooltipFieldConfig {
  /** The key from the API response data or a special key prefixed with _ */
  key: string;
  /** Display label (overrides auto-capitalize) */
  label?: string;
  /** Prefix to prepend (e.g., "$", "Rs.") */
  prefix?: string;
  /** Suffix to append (e.g., "%", " units") */
  suffix?: string;
  /** Number of decimal places (default: 0) */
  decimals?: number;
  /** Formatting strategy */
  format?: 'raw' | 'commas' | 'shorthand' | 'shorthandOneDecimal' | 'commasOneDecimal';
  /** Whether to auto-capitalize the key as a fallback label (default: true) */
  autoCapitalize?: boolean;
  /** When true, format values using formatStat (K/M/B shorthand). Overrides format. */
  useFormatStat?: boolean;
}

export interface TooltipConfig {
  enabled?: boolean;
  formatValues?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  padding?: number | number[];
  textStyle?: {
    color?: string;
    fontSize?: number;
  };
  /** Trigger type: 'item' or 'axis' */
  trigger?: 'item' | 'axis';
  /** Fields to display in tooltip, in order */
  fields?: TooltipFieldConfig[];
  /** Title template. Supports {param.name}, {param.seriesName} placeholders */
  titleTemplate?: string;
  /** When true, auto-capitalize all API keys used as labels (default: true) */
  autoCapitalizeKeys?: boolean;
  /** Show percentage for pie/donut (default: false) */
  showPercentage?: boolean;
}

export interface AxisConfig {
  /** Prefix to prepend to axis labels (e.g., "$") */
  prefix?: string;
  /** Suffix to append to axis labels (e.g., "%", " units") */
  suffix?: string;
  /** When true, format values using formatStat (K/M/B shorthand) */
  useFormatStat?: boolean;
  /** Number of decimal places */
  decimals?: number;
  /** Show axis labels (default: true) */
  show?: boolean;
  /** Axis name/title (e.g., "Revenue ($)") */
  name?: string;
  /** Name position */
  nameLocation?: 'start' | 'center' | 'end';
  /** Name gap from axis */
  nameGap?: number;
}

export interface LegendConfig {
  show: boolean;
  data: string[];
  type?: 'plain' | 'scroll';
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
  orient?: 'horizontal' | 'vertical';
  pageButtonItemGap?: number;
  pageButtonGap?: number;
  pageIconSize?: number;
}

export interface GridConfig {
  left: string;
  right: string;
  top: string;
  bottom: string;
  containLabel: boolean;
}

export interface SeriesConfig {
  name: string;
  type: string;
  stack?: string;
  emphasis?: { focus: string };
  data: any[];
  barWidth?: string | number;
  itemStyle?: { color?: string; opacity?: number };
  label?: LabelConfig;
}

export interface LabelConfig {
  show: boolean;
  position: string;
  formatter?: (params: any) => string;
  fontSize?: number;
  color?: string;
  fontWeight?: string;
}

// ============ LEGEND UTILITIES ============

/**
 * Build legend configuration based on chart config
 */
export function buildLegendConfig(seriesNames: string[], config: ChartConfig): LegendConfig {
  const showLegend = config.showLegend !== false;
  const legendPosition = config.legendPosition || 'top';
  const legendScroll = config.legendScroll !== false;

  const legendConfig: LegendConfig = {
    show: showLegend,
    data: [...seriesNames].reverse(),
    type: legendScroll ? 'scroll' : 'plain',
  };

  // Position configuration
  switch (legendPosition) {
    case 'top':
      legendConfig.top = 0;
      break;
    case 'bottom':
      legendConfig.bottom = 0;
      break;
    case 'left':
      legendConfig.left = 0;
      legendConfig.orient = 'vertical';
      break;
    case 'right':
      legendConfig.right = 0;
      legendConfig.orient = 'vertical';
      break;
  }

  // Pagination settings
  if (config.legendPageSize) {
    legendConfig.pageButtonItemGap = 5;
    legendConfig.pageButtonGap = 10;
    legendConfig.pageIconSize = 12;
  }

  return legendConfig;
}

// ============ GRID UTILITIES ============

/**
 * Default ECharts grid config for responsive charts.
 * Uses percentage-based values with containLabel so the chart
 * adapts to any container size (compact no-scroll or full-scroll).
 * Override individual properties via the optional parameter.
 *
 * Usage: `grid: getDefaultChartGrid()` or `grid: getDefaultChartGrid({ bottom: '20%' })`
 */
export function getDefaultChartGrid(overrides?: Partial<GridConfig>): GridConfig {
  return {
    top: '10%',
    left: '2%',
    right: '4%',
    bottom: '15%',
    containLabel: true,
    ...overrides,
  };
}

/**
 * Build grid configuration based on legend position
 */
export function buildGridConfig(
  legendPosition: string = 'top',
  showLegend: boolean = true,
  customConfig?: Partial<GridConfig>,
): GridConfig {
  const defaultConfig: GridConfig = {
    left: '3%',
    right: '4%',
    top: legendPosition === 'top' && showLegend ? '12%' : '5%',
    bottom: '3%',
    containLabel: true,
  };

  return { ...defaultConfig, ...customConfig };
}

// ============ TOOLTIP UTILITIES ============

/**
 * Build default tooltip configuration.
 *
 * @param customConfig - per-chart tooltip config from widget JSON
 * @param themeTooltip - optional theme tooltip tokens (bg, borderColor, textColor, fontSize).
 *                      When provided, replaces the hardcoded defaults.
 */
export function buildTooltipConfig(
  customConfig?: TooltipConfig,
  themeTooltip?: { bg?: string; borderColor?: string; textColor?: string; fontSize?: number },
): any {
  const defaultConfig = {
    trigger: customConfig?.trigger || 'item',
    backgroundColor: themeTooltip?.bg || '#fff',
    borderColor: themeTooltip?.borderColor || '#ccc',
    borderWidth: 1,
    padding: [10, 14],
    textStyle: {
      color: themeTooltip?.textColor || '#333',
      fontSize: themeTooltip?.fontSize || 13,
    },
  };

  if (!customConfig) return defaultConfig;

  const result: any = {
    ...defaultConfig,
    ...(customConfig.backgroundColor && { backgroundColor: customConfig.backgroundColor }),
    ...(customConfig.borderColor && { borderColor: customConfig.borderColor }),
    ...(customConfig.borderWidth !== undefined && { borderWidth: customConfig.borderWidth }),
    ...(customConfig.padding && { padding: customConfig.padding }),
    ...(customConfig.textStyle && {
      textStyle: { ...defaultConfig.textStyle, ...customConfig.textStyle },
    }),
  };

  // Build formatter if fields are configured
  const formatter = buildConfigurableTooltipFormatter(customConfig);
  if (formatter) {
    result.formatter = formatter;
  }

  return result;
}

/**
 * Build tooltip formatter for stacked bar charts
 */
export function buildStackedBarTooltipFormatter(
  stackType: 'normal' | 'percentage',
  totals: number[],
  seriesData: any[],
  labels?: {
    categoryLabel?: string;
    seriesLabel?: string;
    valueLabel?: string;
    percentageLabel?: string;
    totalLabel?: string;
  },
): (params: any) => string {
  // UI Standards (Point 2): keys in Capital Case, Bold, Left-aligned, key-value format.
  const categoryLabel = formatValueSafely(labels?.categoryLabel || 'Category', 'capital');
  const seriesLabel = formatValueSafely(labels?.seriesLabel || 'Series', 'capital');
  const valueLabel = formatValueSafely(labels?.valueLabel || 'Value', 'capital');
  const percentageLabel = formatValueSafely(labels?.percentageLabel || 'Percentage', 'capital');
  const totalLabel = formatValueSafely(labels?.totalLabel || 'Total', 'capital');

  return (params: any) => {
    if (params.seriesName === 'Total') return '';

    const categoryIndex = params.dataIndex;
    const total = totals[categoryIndex];

    if (stackType === 'percentage') {
      const originalSeries = seriesData.find((s) => s.name === params.seriesName);
      const originalValue = originalSeries?.data[categoryIndex] || 0;

      return `
        <div style="text-align:left; line-height: 1.8;">
          <div><b>${categoryLabel}:</b> ${params.name}</div>
          <div><b>${seriesLabel}:</b> ${params.seriesName}</div>
          <div><b>${percentageLabel}:</b> ${params.value.toFixed(1)}%</div>
          <div><b>${valueLabel}:</b> ${formatNumberWithCommas(originalValue)}</div>
          <div><b>${totalLabel}:</b> ${formatNumberWithCommas(total)}</div>
        </div>
      `;
    }

    return `
      <div style="text-align:left; line-height: 1.8;">
        <div><b>${categoryLabel}:</b> ${params.name}</div>
        <div><b>${seriesLabel}:</b> ${params.seriesName}</div>
        <div><b>${valueLabel}:</b> ${formatNumberWithCommas(params.value)}</div>
        <div><b>${totalLabel}:</b> ${formatNumberWithCommas(total)}</div>
      </div>
    `;
  };
}

// ============ CONFIGURABLE TOOLTIP UTILITIES ============

/**
 * Format a single tooltip value according to a TooltipFieldConfig.
 */
export function formatTooltipValue(value: any, fieldConfig?: TooltipFieldConfig): string {
  if (value === null || value === undefined || value === '') return '-';
  if (!fieldConfig) return String(value);

  const num = Number(value);
  const isNumeric = !isNaN(num);

  let formatted: string;

  if (!isNumeric) {
    formatted = String(value);
  } else if (fieldConfig.useFormatStat) {
    // useFormatStat: true → use formatStat with prefix baked in (K/M/B shorthand)
    return formatStat(num, '', fieldConfig.prefix || '') + (fieldConfig.suffix || '');
  } else {
    const format = fieldConfig.format || 'raw';
    const decimals = fieldConfig.decimals ?? 0;

    switch (format) {
      case 'commas':
        formatted = formatDecimalWithCommas(num);
        break;
      case 'commasOneDecimal':
        formatted = oneDecimalWithCommas(num);
        break;
      case 'shorthand':
        formatted = formatStat(num);
        break;
      case 'shorthandOneDecimal':
        formatted = formatStatOneDecimal(num);
        break;
      case 'raw':
      default:
        formatted = decimals > 0 ? roundOff(num, decimals) : String(num);
        break;
    }
  }

  return wrapWithPrefixSuffix(formatted, fieldConfig.prefix || '', fieldConfig.suffix || '');
}

/**
 * Get the display label for a tooltip field.
 * Uses explicit label if provided, otherwise auto-capitalizes the key.
 */
export function getTooltipLabel(fieldConfig: TooltipFieldConfig, autoCapitalize: boolean = true): string {
  // UI Standards (Point 2): enforce Capital Case on tooltip keys even when a
  // custom label is provided via widget JSON — unless autoCapitalize is explicitly disabled.
  const shouldCapitalize = autoCapitalize !== false && fieldConfig.autoCapitalize !== false;
  if (fieldConfig.label) {
    return shouldCapitalize ? formatValueSafely(fieldConfig.label, 'capital') : fieldConfig.label;
  }
  // Strip leading underscore from special keys for auto-capitalize
  const cleanKey = fieldConfig.key.startsWith('_') ? fieldConfig.key.slice(1) : fieldConfig.key;
  return shouldCapitalize ? capitalizeKey(cleanKey) : cleanKey;
}

/**
 * Build a configurable ECharts tooltip formatter from a TooltipConfig.
 * Returns null if no fields are configured, allowing the chart to use its default.
 */
export function buildConfigurableTooltipFormatter(
  tooltipConfig?: TooltipConfig,
): ((params: any) => string) | null {
  if (!tooltipConfig?.fields || tooltipConfig.fields.length === 0) {
    return null;
  }

  const autoCapitalize = tooltipConfig.autoCapitalizeKeys !== false;

  return (params: any) => {
    const data = Array.isArray(params) ? params[0] : params;
    const dataObj = data?.data || {};

    const lines: string[] = [];

    if (tooltipConfig.titleTemplate) {
      let title = tooltipConfig.titleTemplate;
      title = title.replace('{param.name}', data?.name || '');
      title = title.replace('{param.seriesName}', data?.seriesName || '');
      title = title.replace('{param.value}', data?.value ?? '');
      lines.push(`<div style="text-align:left;"><b>${title}</b></div>`);
    }

    for (const field of tooltipConfig.fields!) {
      const label = getTooltipLabel(field, autoCapitalize);

      let rawValue = dataObj[field.key];
      if (rawValue === undefined) {
        if (field.key === '_name' || field.key === '_category') rawValue = data?.name;
        else if (field.key === '_value') rawValue = data?.value;
        else if (field.key === '_seriesName' || field.key === '_series') rawValue = data?.seriesName;
        else if (field.key === '_percent' || field.key === '_percentage') rawValue = data?.percent;
        // For array-index keys (bubble chart: "0", "1", "2")
        else if (!isNaN(Number(field.key)) && Array.isArray(data?.data)) {
          rawValue = data.data[Number(field.key)];
        }
      }

      const formattedValue = formatTooltipValue(rawValue, field);
      // Keys bold + left-aligned per UI Standards (Point 2)
      lines.push(`<div style="text-align:left;"><b>${label}:</b> ${formattedValue}</div>`);
    }

    if (tooltipConfig.showPercentage && data?.percent !== undefined) {
      lines.push(`<div style="text-align:left;"><b>Percentage:</b> ${Number(data.percent).toFixed(1)}%</div>`);
    }

    // Left-aligned wrapper per UI Standards (Point 2)
    return `<div style="text-align:left; line-height: 1.8;">${lines.join('')}</div>`;
  };
}

/**
 * Build a configurable tooltip formatter for stacked bar charts.
 * Supports special keys: _category, _series, _value, _percentage, _total.
 * Falls back to buildStackedBarTooltipFormatter if no fields configured.
 */
export function buildConfigurableStackedTooltipFormatter(
  tooltipConfig: TooltipConfig | undefined,
  stackType: 'normal' | 'percentage',
  totals: number[],
  seriesData: any[],
  defaultLabels?: {
    categoryLabel?: string;
    seriesLabel?: string;
    valueLabel?: string;
    percentageLabel?: string;
    totalLabel?: string;
  },
): (params: any) => string {
  if (tooltipConfig?.fields && tooltipConfig.fields.length > 0) {
    const autoCapitalize = tooltipConfig.autoCapitalizeKeys !== false;

    return (params: any) => {
      if (params.seriesName === 'Total') return '';

      const categoryIndex = params.dataIndex;
      const total = totals[categoryIndex];
      const lines: string[] = [];

      for (const field of tooltipConfig.fields!) {
        const label = getTooltipLabel(field, autoCapitalize);
        let rawValue: any;

        if (field.key === '_category' || field.key === '_name') rawValue = params.name;
        else if (field.key === '_series' || field.key === '_seriesName') rawValue = params.seriesName;
        else if (field.key === '_value') {
          if (stackType === 'percentage') {
            const orig = seriesData.find(s => s.name === params.seriesName);
            rawValue = orig?.data[categoryIndex] || 0;
          } else {
            rawValue = params.value;
          }
        }
        else if (field.key === '_percentage') {
          rawValue = stackType === 'percentage' ? params.value?.toFixed(1) + '%' : undefined;
        }
        else if (field.key === '_total') rawValue = total;
        else rawValue = params.data?.[field.key];

        if (rawValue !== undefined) {
          const formattedValue = formatTooltipValue(rawValue, field);
          // Keys bold + left-aligned per UI Standards (Point 2)
          lines.push(`<div style="text-align:left;"><b>${label}:</b> ${formattedValue}</div>`);
        }
      }

      return `<div style="text-align:left; line-height: 1.8;">${lines.join('')}</div>`;
    };
  }

  return buildStackedBarTooltipFormatter(stackType, totals, seriesData, defaultLabels);
}

/**
 * Build treemap-specific configurable tooltip formatter.
 * Resolves _parent and _child from treePathInfo.
 * Returns null if no fields configured.
 */
export function buildTreemapTooltipFormatter(
  tooltipConfig?: TooltipConfig,
): ((params: any) => string) | null {
  if (!tooltipConfig?.fields || tooltipConfig.fields.length === 0) {
    return null;
  }

  const autoCapitalize = tooltipConfig.autoCapitalizeKeys !== false;

  return (params: any) => {
    const path = params.treePathInfo;
    const parentName = path?.[1]?.name || '';
    const childName = params.name || '';
    const lines: string[] = [];

    for (const field of tooltipConfig.fields!) {
      const label = getTooltipLabel(field, autoCapitalize);
      let rawValue: any;

      if (field.key === '_parent') rawValue = parentName;
      else if (field.key === '_child') rawValue = childName;
      else if (field.key === '_value') rawValue = params.value;
      else rawValue = params.data?.[field.key];

      const formattedValue = formatTooltipValue(rawValue, field);
      // Keys bold + left-aligned per UI Standards (Point 2)
      lines.push(`<div style="text-align:left;"><b>${label}:</b> ${formattedValue}</div>`);
    }

    return `<div style="text-align:left; line-height:1.8;">${lines.join('')}</div>`;
  };
}

// ============ SERIES UTILITIES ============

/**
 * Build series configuration with colors and labels
 */
export function buildSeriesWithLabels(
  seriesData: SeriesConfig[],
  config: ChartConfig,
  totals: number[],
): SeriesConfig[] {
  const colorMap = config.colorMap || {};
  const positionColors = config.topNColorsByStackPosition || [];
  const showDataLabels = config.showDataLabels !== false;
  const dataLabelPosition = config.dataLabelPosition || 'inside';
  const stackType = config.stackType || 'normal';
  const barWidth = config.barWidth;

  const seriesWithLabels = seriesData.map((s, index) => {
    // Determine color: position-based first, then name-based
    let seriesColor: string | null = null;
    if (positionColors.length > 0 && index < positionColors.length) {
      seriesColor = positionColors[index];
    } else if (colorMap[s.name]) {
      seriesColor = colorMap[s.name];
    }

    const seriesConfig: any = {
      ...s,
      ...(barWidth ? { barWidth } : {}),
      ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}),
    };

    if (showDataLabels) {
      seriesConfig.label = {
        show: true,
        position: dataLabelPosition,
        formatter: (params: any) =>
          stackType === 'percentage'
            ? formatPercentageLabel(params.value)
            : formatValueLabel(params.value),
        fontSize: 10,
        color: dataLabelPosition === 'inside'
          ? (config?.dataLabelInsideColor || '#fff')
          : (config?.dataLabelOutsideColor || '#333'),
      };
    }

    return seriesConfig;
  });

  return seriesWithLabels;
}

/**
 * Build total label series for stacked charts
 */
export function buildTotalLabelSeries(
  totals: number[],
  stackType: 'normal' | 'percentage',
  config?: { offset?: [number, number]; totalLabelColor?: string },
): SeriesConfig {
  return {
    name: 'Total',
    type: 'bar',
    stack: 'total',
    data: totals.map(() => 0),
    label: {
      show: true,
      position: 'top',
      formatter: (params: any) =>
        stackType === 'percentage' ? '100%' : formatValueLabel(totals[params.dataIndex]),
      fontSize: 11,
      color: config?.totalLabelColor || '#333',
      fontWeight: 'bold',
    },
    itemStyle: { color: 'transparent' },
  };
}

/**
 * Sort series by total value
 */
export function sortSeriesByTotal(seriesData: any[], order: 'asc' | 'desc' = 'asc'): any[] {
  return [...seriesData].sort((a, b) => {
    const sumA = a.data.reduce((sum: number, item: any) => {
      const val = item != null && typeof item === 'object' ? (item.value ?? 0) : (item || 0);
      return sum + val;
    }, 0);
    const sumB = b.data.reduce((sum: number, item: any) => {
      const val = item != null && typeof item === 'object' ? (item.value ?? 0) : (item || 0);
      return sum + val;
    }, 0);
    return order === 'asc' ? sumA - sumB : sumB - sumA;
  });
}

/**
 * Calculate totals for each category
 */
export function calculateCategoryTotals(categories: string[], seriesData: any[]): number[] {
  return categories.map((_, catIndex) => {
    return seriesData.reduce((sum, s) => {
      const item = s.data[catIndex];
      const val = item != null && typeof item === 'object' ? (item.value ?? 0) : (item || 0);
      return sum + val;
    }, 0);
  });
}

/**
 * Convert series data to percentage
 */
export function convertToPercentage(seriesData: any[], totals: number[]): any[] {
  return seriesData.map((s) => ({
    ...s,
    data: s.data.map((item: any, idx: number) => {
      const val = item != null && typeof item === 'object' ? (item.value ?? 0) : (item || 0);
      const total = totals[idx];
      return total > 0 ? (val / total) * 100 : 0;
    }),
  }));
}

// ============ AXIS UTILITIES ============

/**
 * Build X-axis configuration
 */
export function buildXAxisConfig(categories: string[], enableClickEvents: boolean = true): any {
  return {
    type: 'category',
    data: categories,
    triggerEvent: enableClickEvents,
    axisLabel: {
      clickable: enableClickEvents,
    },
  };
}

/**
 * Build a category-axis label formatter that applies Capital/Camel case
 * per UI Standards (Point 4). Numbers and quarter codes (Q1..Q4) are passed through.
 *
 * Usage in chart components:
 *   xAxis: { ..., axisLabel: { formatter: buildCategoryAxisFormatter(data?.textCase) } }
 */
export function buildCategoryAxisFormatter(mode: TextCaseMode = 'capital'): (value: any) => string {
  return (value: any) => formatValueSafely(value, mode);
}

/**
 * Format legend item names per UI Standards (Point 4).
 * Pass the formatter to legend.formatter in the ECharts option.
 */
export function buildLegendNameFormatter(mode: TextCaseMode = 'capital'): (name: string) => string {
  return (name: string) => formatValueSafely(name, mode);
}

/**
 * Build configurable axis label from an AxisConfig.
 * Returns an object with `formatter` and/or `show` to spread into `axisLabel`,
 * or undefined if no config is provided.
 */
export function buildConfigurableAxisLabel(
  axisConfig?: AxisConfig,
): { formatter?: (value: number) => string; show?: boolean } | undefined {
  if (!axisConfig) return undefined;

  const result: any = {};
  if (axisConfig.show === false) result.show = false;

  if (axisConfig.useFormatStat || axisConfig.prefix || axisConfig.suffix) {
    result.formatter = (value: number) => {
      if (axisConfig.useFormatStat) {
        return formatStat(value, '', axisConfig.prefix || '') + (axisConfig.suffix || '');
      }
      const decimals = axisConfig.decimals ?? 0;
      const formatted = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
      return (axisConfig.prefix || '') + formatted + (axisConfig.suffix || '');
    };
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Build Y-axis configuration
 */
export function buildYAxisConfig(
  stackType: 'normal' | 'percentage',
  showLabels: boolean = true,
  axisConfig?: AxisConfig,
): any {
  const configurableLabel = buildConfigurableAxisLabel(axisConfig);

  return {
    type: 'value',
    axisLabel: {
      show: showLabels,
      formatter: stackType === 'percentage'
        ? '{value}%'
        : configurableLabel?.formatter || undefined,
      ...(configurableLabel?.show === false ? { show: false } : {}),
    },
    ...(stackType === 'percentage' ? { max: 100 } : {}),
    ...(axisConfig?.name ? { name: axisConfig.name, nameLocation: axisConfig.nameLocation || 'center', nameGap: axisConfig.nameGap || 40 } : {}),
  };
}

// ============ FORMAT UTILITIES ============

/**
 * Format value for data labels (K, M notation)
 */
export function formatValueLabel(value: number): string {
  if (value === 0) return '';
  if (value >= 1000000) return Math.round(value / 1000000) + 'M';
  if (value >= 1000) return Math.round(value / 1000) + 'K';
  return String(value);
}

/**
 * Format percentage for data labels
 */
export function formatPercentageLabel(value: number): string {
  if (value === 0) return '';
  return Math.round(value) + '%';
}

/**
 * Format number with commas (US locale)
 */
export function formatNumberWithCommas(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format number with commas (no decimals)
 */
export function formatIntegerWithCommas(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

// ============ LEGEND SELECTION UTILITIES ============

/**
 * Build legend selected state object
 */
export function buildLegendSelectedState(
  allSeriesNames: string[],
  selectedIndices: number[],
): Record<string, boolean> {
  const selected: Record<string, boolean> = {};

  if (selectedIndices.length === 0) {
    // Show all
    allSeriesNames.forEach((name) => {
      selected[name] = true;
    });
  } else {
    // Hide all first
    allSeriesNames.forEach((name) => {
      selected[name] = false;
    });
    // Show only selected
    selectedIndices.forEach((index) => {
      if (index >= 0 && index < allSeriesNames.length) {
        selected[allSeriesNames[index]] = true;
      }
    });
  }

  return selected;
}

/**
 * Apply opacity to series data for category highlighting
 */
export function applyOpacityToSeries(
  series: any[],
  highlightedIndices: number[],
  highlightOpacity: number = 1,
  dimOpacity: number = 0,
): any[] {
  return series.map((s: any) => {
    if (s.name === 'Total') return s;

    return {
      ...s,
      data: s.data?.map((d: any, idx: number) => {
        const isHighlighted = highlightedIndices.length === 0 || highlightedIndices.includes(idx);
        const opacity = isHighlighted ? highlightOpacity : dimOpacity;

        if (typeof d === 'object' && d !== null) {
          return { ...d, itemStyle: { ...d.itemStyle, opacity } };
        }
        return { value: d, itemStyle: { opacity } };
      }),
    };
  });
}

/**
 * Reset opacity for all series data
 */
export function resetSeriesOpacity(series: any[]): any[] {
  return series.map((s: any) => {
    if (s.name === 'Total') return s;

    return {
      ...s,
      data: s.data?.map((d: any) => {
        if (typeof d === 'object' && d !== null) {
          const newItemStyle = { ...d.itemStyle };
          delete newItemStyle.opacity;
          return {
            ...d,
            itemStyle: Object.keys(newItemStyle).length ? newItemStyle : undefined,
          };
        }
        return d;
      }),
    };
  });
}
